import { RouteItem, expected } from '@aloft/toolkit-shared';

import { useCallback } from 'react';

import { useAlertsRef } from '../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectTriggeredAlerts } from '../redux/features/selectors/uiSelectors.ts';
import {
  getActiveAlertsForRoute,
  useAppraisifyNavigation,
} from './validation/useAppraisifyNavigation.ts';
import { selectCommentaryV2 } from '../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsInspectifyOrder } from '../redux/features/selectors/orderSelectors.ts';
import { processAlertsForRoute, retriggerAlertToasts } from '../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';
import { useModuleContext } from '../modules/shared/moduleContext/useModuleContext.ts';

const deferNavigate = (navigate: (path: string) => void, path: string) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      navigate(path);
    });
  });
};

export const useAppraisifyNavAlertBlock = () => {
  const dispatch = useAppDispatch();
  const alertsRef = useAlertsRef();
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const {
    getCurrentAppraisifyRoute,
    getForwardNavigationRedirectRoute,
    getRoutesWithActiveAlertsBetweenNav,
    isForwardAppraisifyNavigation,
  } = useAppraisifyNavigation();
  const {
    navigation: { useLocation, useNavigate, useParams },
  } = useModuleContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { orderId } = useParams();

  const getRedirectRoute = useCallback(
    (targetRoute: RouteItem) => {
      if (!isInspectifyOrder) {
        return undefined;
      }
      const currentRoute = getCurrentAppraisifyRoute(pathname);
      if (!currentRoute || currentRoute === targetRoute) {
        return undefined;
      }
      if (!isForwardAppraisifyNavigation(currentRoute, targetRoute)) {
        return undefined;
      }
      return getForwardNavigationRedirectRoute(currentRoute, targetRoute, alertsRef.current);
    },
    [
      alertsRef,
      getCurrentAppraisifyRoute,
      getForwardNavigationRedirectRoute,
      isForwardAppraisifyNavigation,
      isInspectifyOrder,
      pathname,
    ],
  );

  const wouldBlockForwardNavigation = useCallback(
    (targetRoute: RouteItem) => Boolean(getRedirectRoute(targetRoute)),
    [getRedirectRoute],
  );

  const blockForwardNavigationIfNeeded = useCallback(
    (targetRoute: RouteItem) => {
      const redirectRoute = getRedirectRoute(targetRoute);
      if (!redirectRoute) {
        return false;
      }

      const alerts = alertsRef.current;
      const currentRoute = getCurrentAppraisifyRoute(pathname);
      if (!currentRoute) {
        return false;
      }

      const routesWithAlerts = getRoutesWithActiveAlertsBetweenNav(
        currentRoute,
        targetRoute,
        alerts,
      );
      const alertTypesToShow = [
        ...new Set([
          ...routesWithAlerts.flatMap((route) =>
            getActiveAlertsForRoute(alerts, route, alertsCommentary).map((alert) => alert.type),
          ),
          ...(redirectRoute !== currentRoute
            ? getActiveAlertsForRoute(alerts, redirectRoute, alertsCommentary).map(
                (alert) => alert.type,
              )
            : []),
        ]),
      ];

      if (alertTypesToShow.length > 0) {
        dispatch(retriggerAlertToasts(alertTypesToShow));
      }

      routesWithAlerts.forEach((route) => {
        dispatch(
          processAlertsForRoute({
            route,
            alertsPerSection: alerts,
            alertsCommentary,
            openAlertsPanel: redirectRoute !== currentRoute && route === redirectRoute,
          }),
        );
      });

      if (redirectRoute !== currentRoute) {
        deferNavigate(
          (path) => navigate(path, { replace: true }),
          `/order/${expected(orderId)}/paired-cost/${redirectRoute}`,
        );
      }
      return true;
    },
    [
      alertsCommentary,
      alertsRef,
      dispatch,
      getCurrentAppraisifyRoute,
      getRedirectRoute,
      getRoutesWithActiveAlertsBetweenNav,
      navigate,
      orderId,
      pathname,
    ],
  );

  const shouldShowNavAlertTooltip = useCallback(
    (targetRoute: RouteItem, alertCount: number) =>
      isInspectifyOrder &&
      triggeredAlerts.length > 0 &&
      (alertCount > 0 || wouldBlockForwardNavigation(targetRoute)),
    [isInspectifyOrder, triggeredAlerts.length, wouldBlockForwardNavigation],
  );

  return { blockForwardNavigationIfNeeded, shouldShowNavAlertTooltip };
};
